'use client';

import { useEffect, useRef, useState } from 'react';

import { encodeGif } from '@/lib/browser-gif-encoder';

export default function BrowserPngToGif() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('image');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    };
  }, [downloadUrl]);

  function handleFile(file: File | undefined) {
    setError(null);
    setInfo(null);
    if (!file) return;
    if (typeof window === 'undefined') return;
    setFileName(file.name.replace(/\.[^.]+$/, '') || 'image');
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setError('That file could not be read as an image. Try another PNG.');
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    if (typeof document === 'undefined') return;
    const canvas = canvasRef.current;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Your browser blocked the canvas needed to convert this image.');
      }
      // This encoder writes opaque GIF frames, so flatten transparency onto white.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = encodeGif(imageData.data, canvas.width, canvas.height);
      const blob = new Blob([result.bytes], { type: 'image/gif' });
      setDownloadUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setInfo(
        `Single-frame GIF, ${canvas.width}x${canvas.height}px, ${result.paletteSize}-color palette, ${(result.bytes.length / 1024).toFixed(0)} KB.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not encode the GIF file.');
    }
  }, [image]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">GIF</span>
        <h2 className="tool-live-title">Convert PNG to GIF</h2>
        <p className="tool-live-subtitle">
          Upload a PNG and download a single-frame GIF. Everything runs in your browser and nothing
          is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose a PNG image</span>
            <input
              accept="image/png,.png"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}.gif`}
                href={downloadUrl}
              >
                Download GIF
              </a>
            ) : null}
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {info && !error ? <p className="tool-live-panel-note">{info}</p> : null}
          {image || error ? null : <p className="tool-live-panel-note">Pick a PNG to convert.</p>}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            GIF supports only 1-bit transparency, so transparent pixels are flattened onto white and
            colors are reduced to a 256-color palette. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
